import { useId } from 'react';
import type { ReactNode } from 'react';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?:
    | 'text'
    | 'email'
    | 'tel'
    | 'date'
    | 'time'
    | 'password'
    | 'number'
    | 'textarea'
    | 'select';
  options?: FormFieldOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  hint?: ReactNode;
  autoFocus?: boolean;
  autoComplete?: string;
  /** Fylder hele bredden i .form-grid. */
  wide?: boolean;
}

export function FormField({
  label,
  value,
  onChange,
  type = 'text',
  options = [],
  placeholder,
  required,
  disabled,
  error,
  hint,
  autoFocus,
  autoComplete,
  wide,
}: FormFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const shared = {
    id,
    value,
    disabled,
    placeholder,
    autoFocus,
    required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
  };

  return (
    <div className={`form-field${wide ? ' span-2' : ''}${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required && (
          <span className="required-mark" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {type === 'textarea' ? (
        <textarea {...shared} onChange={(event) => onChange(event.target.value)} />
      ) : type === 'select' ? (
        <select {...shared} onChange={(event) => onChange(event.target.value)}>
          <option value="">– vælg –</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...shared}
          type={type}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {error && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
      {!error && hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}
